<template>
  <div :class="containerClass">
    <page-header :title="$route.params.id ? 'Edit Testimonial' : 'Add New Testimonial'" :container-class="null" class="mb-16pt" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Quote" label-for="quote" label-class="form-label" description="Their own words. Real quotes only.">
        <b-form-textarea id="quote" :rows="4" v-model.trim="form.quote" required />
      </b-form-group>

      <b-form-row>
        <b-col md="6">
          <b-form-group label="Name" label-for="name" label-class="form-label">
            <b-form-input id="name" placeholder="e.g. Maria G." v-model.trim="form.name" required />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Role" label-for="role" label-class="form-label">
            <b-form-input id="role" placeholder="e.g. Accepted to an ADN program" v-model.trim="form.role" />
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group label="University" label-for="university" label-class="form-label" description="The college they were accepted to. Shows its logo and links to its TransitionU page.">
        <v-select
          id="university"
          class="form-control v-select-custom"
          label="name"
          v-model="form.university"
          :reduce="u => u.id"
          placeholder="Type to search schools"
          :options="universityOptions"
          :loading="areUniversitiesLoading"
          :filterable="false"
          @search="onUniversitySearch"
        >
          <template #option="u">{{ u.name }}<span class="text-muted small"> · {{ [u.city, u.state].filter(Boolean).join(', ') }}</span></template>
        </v-select>
      </b-form-group>

      <b-form-group label="School (optional)" label-for="school" label-class="form-label" description="Free text, e.g. where they did their CNA.">
        <b-form-input id="school" v-model.trim="form.school" />
      </b-form-group>

      <b-form-group label="Path" label-for="pathway" label-class="form-label" description="The path they took, if any.">
        <v-select id="pathway" class="form-control v-select-custom" label="title" v-model="form.pathway" :reduce="p => p.id"
          placeholder="Select a path" :options="pathwayOptions" :loading="arePathwaysLoading" />
      </b-form-group>

      <b-form-group label="Photo" label-for="tt_image" label-class="form-label" description="A real photo of the person, square works best.">
        <div class="d-flex align-items-start" style="gap: 16px">
          <img v-if="form.image_url" :src="form.image_url" alt="" style="width: 100px; height: 100px; object-fit: cover; border-radius: 50%; border: 1px solid #e5e7eb" />
          <div class="flex-grow-1">
            <b-form-file id="tt_image" v-model="imageFile" accept="image/*" placeholder="Choose an image" :disabled="isImageUploading" @input="uploadImage" />
            <b-progress v-if="isImageUploading" :value="imagePercent" :max="100" class="mt-2" height="6px" />
            <b-form-input class="mt-2" type="url" placeholder="or paste an image URL" v-model.trim="form.image_url" />
          </div>
        </div>
      </b-form-group>

      <b-form-row>
        <b-col md="3">
          <b-form-group label="Order" label-for="order" label-class="form-label" description="Lower shows first.">
            <b-form-input id="order" type="number" min="0" v-model.number="form.order" />
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="form.is_visible" name="is-visible-switch" switch>Visible on TransitionU</b-form-checkbox>
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading || isImageUploading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Page from '@/components/Page.vue';

export default {
  components: { PageHeader },
  name: 'PostTuTestimonial',
  extends: Page,

  data() {
    return {
      isLoading: false,
      areUniversitiesLoading: false,
      arePathwaysLoading: false,
      isImageUploading: false,
      imagePercent: 0,
      imageFile: null,
      universityOptions: [],
      pathwayOptions: [],
      form: {
        quote: '',
        name: '',
        role: '',
        school: '',
        university: null,
        image_url: '',
        pathway: null,
        is_visible: false,
        order: 0,
      },
    };
  },

  methods: {
    ...mapActions('tuTestimonials', ['getTuTestimonial', 'createTuTestimonial', 'updateTuTestimonial']),
    ...mapActions('uni', ['getAllUnis', 'getUniImageUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('pathways', ['getAllPathways']),

    async searchUniversities(term) {
      this.areUniversitiesLoading = true;
      try {
        const response = await this.getAllUnis({ limit: 30, ...(term && { search: term }) });
        const rows = get(response, 'data.results', []).map(u => ({ id: u.id, name: u.name, city: u.city, state: u.state }));
        const chosen = this.universityOptions.filter(o => o.id === this.form.university && !rows.some(r => r.id === o.id));
        this.universityOptions = [...rows, ...chosen];
      } catch (_err) {
        /* keep what we have */
      }
      this.areUniversitiesLoading = false;
    },
    onUniversitySearch: debounce(function(term) {
      if (term && term.length >= 2) this.searchUniversities(term);
    }, 350),

    async fetchPathways() {
      this.arePathwaysLoading = true;
      try {
        const response = await this.getAllPathways({ limit: 500, ordering: 'title' });
        this.pathwayOptions = get(response, 'data.results', []).map(p => ({ id: p.id, title: p.title }));
      } catch (_err) {
        this.pathwayOptions = [];
      }
      this.arePathwaysLoading = false;
    },

    async uploadImage(file) {
      if (!file) return;
      this.isImageUploading = true;
      this.imagePercent = 0;
      try {
        const urlResp = await this.getUniImageUploadPresignedUrl({ file_name: file.name, content_type: file.type, upload_type: 'pathways' });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file,
          config: { onUploadProgress: e => { this.imagePercent = Math.round((e.loaded * 100) / e.total); } },
        });
        this.form.image_url = urlResp.upload_url.split('?')[0];
        this.makeToast({ variant: 'success', msg: 'Image uploaded.' });
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: 'Upload failed. Try again or paste a URL.' });
      }
      this.isImageUploading = false;
      this.imageFile = null;
    },

    async loadTestimonial(id) {
      try {
        const response = await this.getTuTestimonial(id);
        const data = get(response, 'data', {});
        const uni = data.university || null;
        this.form = {
          quote: data.quote || '',
          name: data.name || '',
          role: data.role || '',
          school: data.school || '',
          university: uni ? uni.id : null,
          image_url: data.image_url || '',
          pathway: data.pathway ? data.pathway.id : null,
          is_visible: !!data.is_visible,
          order: data.order || 0,
        };
        if (uni) this.universityOptions = [{ id: uni.id, name: uni.name }];
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async onSubmit() {
      this.isLoading = true;
      try {
        const payload = { ...this.form, order: Number(this.form.order) || 0 };
        if (this.$route.params.id) {
          await this.updateTuTestimonial({ id: this.$route.params.id, data: payload });
          this.makeToast({ variant: 'success', msg: 'Testimonial updated.' });
        } else {
          await this.createTuTestimonial(payload);
          this.makeToast({ variant: 'success', msg: 'Testimonial created.' });
        }
        this.$router.push({ name: 'sa-manage-tu-testimonials' });
      } catch (err) {
        const fieldErrors = get(err, 'response.data');
        let msg = this.$t('generalMsgs.genErrorMsg');
        if (fieldErrors && typeof fieldErrors === 'object') {
          const firstKey = Object.keys(fieldErrors)[0];
          const firstVal = fieldErrors[firstKey];
          if (firstKey) msg = `${firstKey}: ${Array.isArray(firstVal) ? firstVal[0] : String(firstVal)}`;
        }
        this.makeToast({ variant: 'danger', msg });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    await this.fetchPathways();
    if (this.$route.params.id) {
      await this.loadTestimonial(this.$route.params.id);
    }
  },
};
</script>
